import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WaitlistCTA from "@/components/landing/WaitlistCTA";
import { HeartScribble } from "@/components/landing/Logo";
import useReveal from "@/hooks/useReveal";
import useSEO from "@/hooks/useSEO";

const weekly = [
    "Compatibility quiz",
    "Identity and photo check",
    "Five Monday matches",
    "Conversation nudges",
    "Date spot suggestions",
];

const dimensions = [
    "Values",
    "Humor",
    "Conflict",
    "Life pace",
    "Affection",
    "Curiosity",
    "Ambition",
    "Social energy",
    "Privacy",
    "Rituals",
    "Distance",
    "Intent",
];

export default function Product() {
    useReveal();
    useSEO({
        title: "Product",
        description:
            "Five good chances. One clear week. Learn about the LÜVYN compatibility system and our weekly Monday ritual.",
        canonical: "/product",
        schema: {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Luvyn Dating Service",
            "description": "Intentional dating and deep compatibility analysis.",
            "brand": {
                "@type": "Brand",
                "name": "Luvyn"
            }
        }
    });

    return (
        <div className="relative min-h-screen overflow-x-hidden" data-testid="product-page">
            <div className="grain" />
            <Header />
            <main id="top">
                <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal in grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-end">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-6">
                                    Product edition · how it works
                                </div>
                                <h1 className="font-display text-[17vw] sm:text-[12vw] lg:text-[10rem] leading-[0.82] tracking-tighter">
                                    Five good{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        chances.
                                    </span>
                                    <br />
                                    One clear week.
                                </h1>
                            </div>
                            <p className="text-lg leading-relaxed text-[var(--ink-soft)] max-w-xl">
                                The product is intentionally small. You tell us what
                                matters, we verify who is real, then every Monday we
                                publish a private issue of five people worth noticing.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper-2)] border-y-2 border-[var(--ink)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
                            <div>
                                <div className="stamp mb-6">Monday ritual</div>
                                <h2 className="font-display text-6xl lg:text-8xl leading-[0.86] tracking-tighter">
                                    Your week,{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        typeset.
                                    </span>
                                </h2>
                            </div>
                            <div className="border-y-2 border-[var(--ink)]">
                                {weekly.map((item, index) => (
                                    <div
                                        key={item}
                                        className="grid grid-cols-[90px_1fr_auto] gap-5 items-center py-7 border-b-2 border-[var(--ink)] last:border-b-0"
                                    >
                                        <span className="font-display text-5xl text-[var(--hot)]">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-display text-3xl lg:text-5xl leading-none">
                                            {item}
                                        </span>
                                        <HeartScribble className="w-7 h-7" color="var(--hot)" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal flex items-end justify-between flex-wrap gap-6 mb-12">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
                                    Compatibility system
                                </div>
                                <h2 className="font-display text-6xl lg:text-9xl leading-[0.85] tracking-tighter">
                                    Twelve quiet{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        signals.
                                    </span>
                                </h2>
                            </div>
                            <p className="max-w-sm text-[var(--ink-soft)] leading-relaxed">
                                We treat compatibility as a pattern of behaviors,
                                boundaries, timing, and emotional habits.
                            </p>
                        </div>

                        <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {dimensions.map((dimension, index) => (
                                <div
                                    key={dimension}
                                    className="bg-[var(--paper)] border-2 border-[var(--ink)] p-6 min-h-[140px] flex flex-col justify-between"
                                >
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--hot)]">
                                        Dim. {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-display text-3xl">
                                        {dimension}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <WaitlistCTA />
            </main>
            <Footer />
        </div>
    );
}
